import { RawAxisProps, SeriesProps } from '@devexpress/dx-react-chart';
import moment from 'moment';
import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardTableModelItem, PregnancyState } from 'shared/api/api';
import { Colors } from 'shared/assets/root-assets/colors';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { WhiteText } from 'web/view/components/Text/white-text';
import PageContainer from 'web/view/components/page-container/page-container';
import { SkioldButton } from 'web/view/components/skiold-components/skiold-button/skiold-button';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { LineChart } from '../../graph/line-graph/line-chart';
import { Heading } from '../../utils/heading';
import { dateFormat } from 'shared/helpers/date-helpers';
import { GetDashboardOverviewGraph } from 'shared/state/ducks/dashboard/operations';
import { ClipLoader } from 'react-spinners';

interface PropsFromParent {
	closeModal: () => void;
}
const getDashboardWeaningsItemsByWeek = (
	listObejct: { [key: string]: DashboardTableModelItem } | undefined,
	object: any,
	propKey: string,
	weaningWeeks: { [key: string]: DashboardTableModelItem } | undefined,
) => {
	// get data for each dashboard item, and separate by week and propKey
	listObejct &&
		Object.keys(listObejct).forEach(key => {
			const val = listObejct[key];
			if (val.matingBatch?.id && weaningWeeks) {
				if (!val.value) {
					return;
				}

				const date = moment(val.matingBatch.matingPeriodStart);
				const key = `${val.matingBatch.batchNumber} (${date.isoWeek()}-${date.year()})`;
				if (!object[key]) {
					object[key] = {};
				}
				object[key][propKey] = val.value ? +val.value.replace(',', '.') : 0;
				object[key].date = date;
			}
		});
};

export const TotalWeaningsLineGraph: FC<PropsFromParent> = React.memo(({ closeModal }) => {
	const dispatch = useDispatch();
	// Get dashboard data
	const dashboardWeaningItems = useSelector((state: WebAppState) =>
		state.dashboardOverview.graphData &&
		state.dashboardOverview.graphData.dashboardTableData &&
		state.dashboardOverview.graphData.dashboardTableData['WeaningDashboard']
			? state.dashboardOverview.graphData.dashboardTableData['WeaningDashboard']
			: [],
	);
	const { graphLoading } = useSelector((state: WebAppState) => state.dashboardOverview);
	const { siteId } = useSelector((state: WebAppState) => state.profile.active!);
	useEffect(() => {
		if (siteId) {
			dispatch(GetDashboardOverviewGraph(siteId, true, PregnancyState.Averted));
		}
	}, [siteId]);

	// Set bar series
	const series = useMemo(() => {
		return [
			{
				argumentField: 'week',
				valueField: 'TotalWeanings',
				name: 'TotalWeanings',
				color: Colors.yellow,
				key: 'TotalWeanings',
				scaleName: 'weanings',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'AvgWeanedPerLitter',
				name: 'AvgWeanedPerLitter',
				color: Colors.pastelBlue,
				key: 'AvgWeanedPerLitter',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'week',
				valueField: 'AvgWeanedPerWeaninig',
				name: 'AvgWeanedPerWeaninig',
				color: Colors.pastelGreen,
				key: 'AvgWeanedPerWeaninig',
				scaleName: 'pcs',
			} as SeriesProps,
		];
	}, []);

	//  set YAxis
	const yAxis = useMemo(() => {
		return [
			{ position: 'right', scaleName: 'weanings', showGrid: false } as RawAxisProps,
			{ position: 'left', scaleName: 'pcs' } as RawAxisProps,
		];
	}, []);

	// generate bar chart data
	const data = useMemo(() => {
		// separate data by dashboard item
		let weaningMatingBatch = dashboardWeaningItems.find(x => x.name === 'WeaningMatingBatch')?.data;
		// The is based on model IWeaningReport
		let totalWeanings = dashboardWeaningItems.find(x => x.name === 'TotalWeanings')?.data;
		let avgWeanedPerLitter = dashboardWeaningItems.find(x => x.name === 'AvgWeanedPerLitter')?.data;
		let avgWeanedPerWeaninig = dashboardWeaningItems.find(x => x.name === 'AvgWeanedPerWeaninig')?.data;

		// create object with week as key
		let objectData = {};

		// get data for each dashboard item
		getDashboardWeaningsItemsByWeek(totalWeanings, objectData, 'TotalWeanings', weaningMatingBatch);
		getDashboardWeaningsItemsByWeek(avgWeanedPerLitter, objectData, 'AvgWeanedPerLitter', weaningMatingBatch);
		getDashboardWeaningsItemsByWeek(avgWeanedPerWeaninig, objectData, 'AvgWeanedPerWeaninig', weaningMatingBatch);

		// map object to array
		let resultArray = Object.keys(objectData).map(key => {
			let week = objectData[key];
			if (week) {
				week.week = key;
				week.TotalBornAvg = week.AvgAlive + week.AvgDead;
			}
			return week;
		});
		return resultArray.slice().sort((a, b) => a.date - b.date);
	}, [dashboardWeaningItems]);

	return (
		<PageContainer>
			<Heading text={localized('Weanings')} />
			<ViewWeb className="default-spacing">
				<ViewWeb className="default-close-pos">
					<SkioldButton theme="grey" onPress={closeModal} title={localized('Close')} />
				</ViewWeb>

				{graphLoading ? (
					<ViewWeb className="spinner-container-active-dashboard">
						<ClipLoader color="#f2ac40" size={70} />
					</ViewWeb>
				) : (
					<ViewWeb className="flex-row">
						<ViewWeb className="vertical-legend-title">
							<div className="rotate-container">
								<WhiteText className="header-white-text rotate-270">{localized('PigsPcs')}</WhiteText>
							</div>
						</ViewWeb>
						<ViewWeb className="flexOne">
							<LineChart
								lineSeries={series}
								chartData={data}
								valueAxis={yAxis}
								title=""
								yTitle={localized('WeaningsPerBatchPcs')}
								keyTranslator={localized}
								zoomPan="both"
								verticalArgumentLabels={true}
							/>
							<WhiteText className="header-white-text">{localized('matingBatch')}</WhiteText>
						</ViewWeb>
					</ViewWeb>
				)}
			</ViewWeb>
		</PageContainer>
	);
});
